import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, CheckCircle2 } from "lucide-react";

const UPDATED = "October 2026";

const CONSENT_TEXT = "I agree to receive SMS text messages from XMD Productions (appointment confirmations, reminders, and updates) at the phone number provided. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe or HELP for help.";

const SAMPLES = [
  {
    label: "Booking confirmation",
    text: "XMD Productions: Hi Marcus, your Recording Studio session is confirmed for Jul 15, 2026 at 2:00 PM (2 hrs). Reply HELP for help, STOP to opt out.",
  },
  {
    label: "Appointment reminder",
    text: "XMD Productions: Reminder — your session is tomorrow, Jul 15 at 2:00 PM, 3020 High Ridge Rd Ste 600A, Boynton Beach. Reply HELP for help, STOP to opt out.",
  },
  {
    label: "Schedule update",
    text: "XMD Productions: Your Jul 15 session has been updated to 3:00 PM per your request. Questions? Reply HELP. Reply STOP to opt out.",
  },
];

export default function SmsConsent() {
  const [demo, setDemo] = useState(false);
  return (
    <div className="pt-28 pb-24 max-w-3xl mx-auto px-5" data-testid="sms-consent-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37] flex items-center gap-2"><MessageSquare size={14} /> SMS Program</p>
      <h1 className="font-display text-4xl md:text-5xl text-white mt-3 mb-2">SMS Opt-In &amp; Consent</h1>
      <p className="text-white/40 text-sm mb-10">Last updated: {UPDATED} · Proof of consent for messaging program</p>

      <div className="space-y-10 text-white/70 text-sm leading-relaxed">
        <section>
          <h2 className="font-display text-xl text-white mb-3">Program Description</h2>
          <p>XMD Productions sends <strong className="text-white">transactional (non-marketing)</strong> SMS text messages to customers who opt in — including booking confirmations, appointment reminders, and schedule updates for sessions booked with our studio (photography, videography, recording &amp; podcast, livestream, and studio rental).</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-white mb-3">How Customers Opt In</h2>
          <p className="mb-4">Consent is collected through an <strong className="text-white">optional, unchecked-by-default</strong> checkbox on our online booking form at <Link to="/book" className="text-[#D4AF37] hover:underline">xmdproductions.com/book</Link> (first step, "Your Details"). Checking the box is never a condition of booking. Each opt-in is logged with a timestamp.</p>

          <label htmlFor="sms-consent-demo" className="flex items-start gap-3 rounded-xl p-4 border border-white/10 bg-[#111] cursor-pointer" data-testid="consent-replica">
            <input id="sms-consent-demo" type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[#D4AF37]" data-testid="consent-replica-checkbox" />
            <span className="text-white/70 text-xs leading-relaxed">
              <span className="text-white/90 font-medium">SMS updates (optional).</span> {CONSENT_TEXT} Consent is not a condition of booking.
            </span>
          </label>
          <p className="text-white/40 text-xs mt-2">↑ Exact opt-in checkbox and wording shown to customers on the booking form. The checkbox is <strong className="text-white/70">NOT pre-selected</strong> — it starts unchecked and the customer must tick it themselves. Customers who leave it unchecked can still book and receive no SMS.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-white mb-3">Sample Messages</h2>
          <div className="space-y-3">
            {SAMPLES.map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-[#111] p-4" data-testid={`sample-${i}`}>
                <p className="font-mono-label uppercase tracking-wide text-[10px] text-[#D4AF37] mb-1.5">{s.label}</p>
                <p className="text-white/80 flex items-start gap-2"><CheckCircle2 size={15} className="text-green-400 mt-0.5 shrink-0" /> {s.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-xl text-white mb-3">Message Frequency, Rates &amp; Opt-Out</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Message frequency varies based on your bookings and interactions.</li>
            <li>Message and data rates may apply.</li>
            <li>Reply <span className="text-[#D4AF37]">STOP</span> at any time to unsubscribe from all messages.</li>
            <li>Reply <span className="text-[#D4AF37]">HELP</span> for help, or contact us at +1 561-503-7743.</li>
            <li>Carriers are not liable for delayed or undelivered messages.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-white mb-3">Privacy</h2>
          <p><strong className="text-white">No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</strong> Information sharing with subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties. See our full <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms &amp; Conditions</Link>.</p>
        </section>

        <section className="pt-4 border-t border-white/10">
          <h2 className="font-display text-xl text-white mb-3">Contact</h2>
          <p>XMD Productions<br />3020 High Ridge Rd, Suite 600A, Boynton Beach, FL 33426<br />Phone: +1 561-503-7743<br />Email: <a href="mailto:info@xmdproductions.com" className="text-[#D4AF37] hover:underline">info@xmdproductions.com</a></p>
        </section>
      </div>
    </div>
  );
}
